import { useQuery } from 'react-query';
import type { JsonValue } from 'type-fest';

import {
  BasePartCardData,
  queryClient,
  useRequest,
  type QueryFunction,
} from '@frontend/api';

type ComponentProcess = {
  id: number;
  name: string;
};

type ComponentMaterial = {
  id: number;
  displayName: string;
};

type OrderItem = {
  id: number;
  created: string;
  orderNumber: number;
};

type ComponentQuoteItem = {
  id: number;
  sentDate: string | null;
  quoteNumberWithRevision: string;
  quoteStatus: string;
  orderItems: OrderItem[];
};

type ComponentLite = {
  id: number;
  partNumber: string;
  revision: string;
  process: ComponentProcess | null;
  material: ComponentMaterial | null;
  quoteItem: ComponentQuoteItem | null;
};

export type PartSearchResultItem = BasePartCardData & {
  /** Part number (from Part.get_part_number). */
  partNumber: string | null;
  /** Part revision (from Part.get_revision). */
  revision: string | null;
  isAssembly: boolean;
  isExportControlled: boolean;
  components: ComponentLite[];
};

export type PartSearchData = {
  count: number;
  next: string | null;
  previous: string | null;
  results: PartSearchResultItem[];
};

type GetPartSearchParams = {
  uuid: string;
  page?: number;
  materialFamily?: number[];
  material?: number[];
  process?: number[];
  account?: number[];
  excludeManual?: boolean;
};

export const getUsePartSearchKey = (params: GetPartSearchParams) => {
  const { uuid, ...rest } = params;
  return ['similarParts', 'search', uuid, JSON.stringify(rest)];
};

const buildQueryParams = (params: GetPartSearchParams) => {
  const query: Record<string, string | number | boolean> = {};

  if (params.page != null) {
    query.page = params.page;
  }
  if (params.materialFamily != null && params.materialFamily.length > 0) {
    query.material_family = params.materialFamily.join(',');
  }
  if (params.material != null && params.material.length > 0) {
    query.material = params.material.join(',');
  }
  if (params.process != null && params.process.length > 0) {
    query.process = params.process.join(',');
  }
  if (params.account != null && params.account.length > 0) {
    query.account = params.account.join(',');
  }
  if (params.excludeManual != null) {
    query.exclude_manual = params.excludeManual;
  }

  return Object.keys(query).length > 0 ? query : undefined;
};

export const usePartSearch: QueryFunction<
  PartSearchData,
  [GetPartSearchParams],
  ReturnType<typeof getUsePartSearchKey>
> = (params, { requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUsePartSearchKey(params),
    queryFn: ({ signal }) =>
      request<PartSearchData & JsonValue>(
        `/similar_parts/search/${params.uuid}`,
        {
          init: {
            method: 'GET',
            signal,
          },
          query: buildQueryParams(params),
        },
      ),
  });
};

export const invalidatePartSearchQuery = (params: GetPartSearchParams) => {
  queryClient.invalidateQueries(getUsePartSearchKey(params));
};
